'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import LiveWebsitePreview from './LiveWebsitePreview';

/**
 * Horizontal rail of client-website cards.
 *
 * The first version used a full-height phone screenshot per card
 * (`aspect-[390/844]` at `85vw`), which on a 390px phone came out around 720px
 * tall — most of a screen for one card, with no sign that more existed. This
 * trades that for a shorter card, a deliberate peek of the next one, and
 * position dots, so the row reads as a browsable set rather than a wall.
 */

type Site = {
    id: string | number;
    url: string;
    title?: string;
    previewMobile?: unknown;
    previewDesktop?: unknown;
};

export default function WebsitesRail({
    sites,
    visitLabel,
    ofLabel,
}: {
    sites: Site[];
    visitLabel: string;
    ofLabel: string;
}) {
    const railRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(0);

    // Derive the active card from scroll position rather than an observer: the
    // rail is short and this stays correct during momentum scrolling, when
    // IntersectionObserver can report several cards at once.
    const syncActive = useCallback(() => {
        const rail = railRef.current;
        if (!rail || rail.children.length === 0) return;

        const railBox = rail.getBoundingClientRect();
        const centre = railBox.left + railBox.width / 2;

        let closest = 0;
        let closestDistance = Infinity;
        Array.from(rail.children).forEach((child, index) => {
            const box = (child as HTMLElement).getBoundingClientRect();
            const distance = Math.abs(box.left + box.width / 2 - centre);
            if (distance < closestDistance) {
                closestDistance = distance;
                closest = index;
            }
        });

        setActive(closest);
    }, []);

    useEffect(() => {
        const rail = railRef.current;
        if (!rail) return;

        syncActive();
        rail.addEventListener('scroll', syncActive, { passive: true });
        window.addEventListener('resize', syncActive);
        return () => {
            rail.removeEventListener('scroll', syncActive);
            window.removeEventListener('resize', syncActive);
        };
    }, [syncActive]);

    const scrollTo = (index: number) => {
        const rail = railRef.current;
        const card = rail?.children[index] as HTMLElement | undefined;
        card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    };

    return (
        <div>
            <div
                ref={railRef}
                /* `snap-center` rather than snap-start so the peek appears on both
                   sides once you are past the first card. */
                className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2 md:gap-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
                {sites.map((site) => (
                    <div
                        key={site.id}
                        className="w-[68vw] shrink-0 snap-center sm:w-[52vw] md:w-[520px]"
                    >
                        <LiveWebsitePreview
                            url={site.url}
                            title={site.title}
                            visitLabel={visitLabel}
                            previewMobile={site.previewMobile as never}
                            previewDesktop={site.previewDesktop as never}
                            /* Card-shaped on phones instead of a full 390:844 screen
                               grab. object-top keeps the hero in frame, so the crop
                               shows the part of the site worth seeing. */
                            className="aspect-[3/4] md:aspect-[1440/900]"
                        />
                    </div>
                ))}
            </div>

            {sites.length > 1 && (
                <div className="mt-5 flex items-center justify-center gap-3">
                    <div className="flex items-center gap-2">
                        {sites.map((site, index) => (
                            <button
                                key={site.id}
                                onClick={() => scrollTo(index)}
                                aria-label={`${site.title || visitLabel} — ${index + 1} ${ofLabel} ${sites.length}`}
                                aria-current={index === active}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    index === active
                                        ? 'w-6 bg-brand-cta'
                                        : 'w-2 bg-brand-text/25 hover:bg-brand-text/50'
                                }`}
                            />
                        ))}
                    </div>
                    <span className="font-heading text-xs tabular-nums text-brand-text/50">
                        {active + 1} {ofLabel} {sites.length}
                    </span>
                </div>
            )}
        </div>
    );
}
